import React, { useState, useMemo } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DollarSign, TrendingDown, TrendingUp, Search, Filter, Calendar,
  Download, ChevronDown, ChevronLeft, ChevronRight, ExternalLink,
  Handshake, UserPlus, Trophy, SkipForward, RefreshCw
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import DateRangeSelect from '@/components/DateRangeSelect';
import InfoTip from '@/components/InfoTip';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import QuickAssignModal from '@/components/QuickAssignModal';

/* Quick Actions */
const QuickActions = ({ mpn, onAssign }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1" data-testid={`action-${mpn}`}>
        Action <ChevronDown className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuItem onClick={() => toast.success('Referred to 1Source')}>
        <Handshake className="h-3.5 w-3.5 mr-2 text-green-600" /> Refer to 1Source
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onAssign?.()}>
        <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-600" /> Assign
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Report Win opened')}>
        <Trophy className="h-3.5 w-3.5 mr-2 text-amber-600" /> Report Win
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Skipped')}>
        <SkipForward className="h-3.5 w-3.5 mr-2 text-gray-500" /> Skip
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

/* Trend sparkline */
function genTrend(base, pct) {
  return Array.from({ length: 20 }, (_, i) => ({
    d: i + 1,
    p: base + (base * pct / 100) * (i / 20) + (Math.random() - 0.5) * base * 0.03,
  }));
}

const PARTS = [
  { mpn: 'STM32F407VGT6', category: 'MCU', distributor: 'DigiKey', bomRef: 'Main Controller Board v2.1', totalSpend: 312500, currentPrice: 12.50, bestRate: 9.20, anchor: 10.80, efficiency: 74, distribEff: 82, distribEfficacy: 68, trend: 5, trendData: genTrend(12.50, 5), renego: 'same', savingsAnchor: 42500, savingsBest: 82500 },
  { mpn: 'MT48LC16M16A2', category: 'Memory IC', distributor: 'Mouser', bomRef: 'Main Controller Board v2.1', totalSpend: 70000, currentPrice: 3.50, bestRate: 2.10, anchor: 2.80, efficiency: 60, distribEff: 78, distribEfficacy: 55, trend: 18, trendData: genTrend(3.50, 18), renego: 'shift', savingsAnchor: 14000, savingsBest: 28000 },
  { mpn: 'ESP32-WROOM-32E', category: 'Wireless IC', distributor: 'Mouser', bomRef: 'IoT Sensor Board v1.0', totalSpend: 70400, currentPrice: 3.20, bestRate: 2.00, anchor: 2.60, efficiency: 81, distribEff: 78, distribEfficacy: 75, trend: 0, trendData: genTrend(3.20, 0), renego: 'same', savingsAnchor: 13200, savingsBest: 26400 },
  { mpn: 'NRF52840-QIAA-R', category: 'MCU', distributor: 'Arrow Electronics', bomRef: 'BLE Module v3.0', totalSpend: 46000, currentPrice: 5.75, bestRate: 4.50, anchor: 5.00, efficiency: 78, distribEff: 75, distribEfficacy: 70, trend: 12, trendData: genTrend(5.75, 12), renego: 'shift', savingsAnchor: 6000, savingsBest: 10000 },
  { mpn: 'TPS54331DR', category: 'Power IC', distributor: 'DigiKey', bomRef: 'Main Controller Board v2.1', totalSpend: 41250, currentPrice: 1.65, bestRate: 1.05, anchor: 1.35, efficiency: 82, distribEff: 82, distribEfficacy: 79, trend: -5, trendData: genTrend(1.65, -5), renego: 'same', savingsAnchor: 7500, savingsBest: 15000 },
  { mpn: 'TPS62130RGTR', category: 'DC-DC', distributor: 'Farnell', bomRef: 'Power Supply Board v1.2', totalSpend: 32250, currentPrice: 2.15, bestRate: 1.65, anchor: 1.90, efficiency: 88, distribEff: 80, distribEfficacy: 82, trend: 0, trendData: genTrend(2.15, 0), renego: 'same', savingsAnchor: 3750, savingsBest: 7500 },
  { mpn: 'USB-C-16P-SMD', category: 'Connector', distributor: 'Arrow Electronics', bomRef: 'IoT Sensor Board v1.0', totalSpend: 19000, currentPrice: 0.38, bestRate: 0.22, anchor: 0.30, efficiency: 79, distribEff: 75, distribEfficacy: 72, trend: 12, trendData: genTrend(0.38, 12), renego: 'same', savingsAnchor: 4000, savingsBest: 8000 },
  { mpn: 'SN65HVD230DR', category: 'CAN Transceiver', distributor: 'Farnell', bomRef: 'CAN Bus Module v2.0', totalSpend: 11400, currentPrice: 0.95, bestRate: 0.65, anchor: 0.80, efficiency: 84, distribEff: 80, distribEfficacy: 78, trend: -3, trendData: genTrend(0.95, -3), renego: 'same', savingsAnchor: 1800, savingsBest: 3600 },
  { mpn: 'GRM188R71H104KA93D', category: 'Capacitor', distributor: 'DigiKey', bomRef: 'IoT Sensor Board v1.0', totalSpend: 2800, currentPrice: 0.028, bestRate: 0.012, anchor: 0.018, efficiency: 64, distribEff: 82, distribEfficacy: 58, trend: -3, trendData: genTrend(0.028, -3), renego: 'same', savingsAnchor: 1000, savingsBest: 1600 },
  { mpn: 'RC0402FR-07100KL', category: 'Resistor', distributor: 'LCSC Electronics', bomRef: 'IoT Sensor Board v1.0', totalSpend: 1500, currentPrice: 0.003, bestRate: 0.002, anchor: 0.0025, efficiency: 83, distribEff: 42, distribEfficacy: 70, trend: 0, trendData: genTrend(0.003, 0), renego: 'shift', savingsAnchor: 250, savingsBest: 500 },
].sort((a, b) => b.savingsAnchor - a.savingsAnchor);

const PAGE_SIZE = 6;
const effBadge = (v) => v >= 80 ? 'bg-green-100 text-green-800' : v >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';

const PartRenegotiations = () => {
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [minSavings, setMinSavings] = useState('');
  const [dateRange, setDateRange] = useState('ltm');
  const [bomFilter, setBomFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [page, setPage] = useState(1);

  const allBoms = useMemo(() => [...new Set(PARTS.map(p => p.bomRef))], []);

  const filtered = useMemo(() => {
    return PARTS.filter(p => {
      const q = searchQuery.toLowerCase();
      if (q && !p.mpn.toLowerCase().includes(q) && !p.distributor.toLowerCase().includes(q) && !p.category.toLowerCase().includes(q)) return false;
      if (minSavings && p.savingsAnchor < Number(minSavings)) return false;
      if (bomFilter !== 'all' && p.bomRef !== bomFilter) return false;
      return true;
    });
  }, [searchQuery, minSavings, bomFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalValueBest = filtered.reduce((s, p) => s + p.savingsBest, 0);
  const totalValueAnchor = filtered.reduce((s, p) => s + p.savingsAnchor, 0);
  const sameSupplierCount = filtered.filter(p => p.renego === 'same').length;
  const shiftSupplierCount = filtered.filter(p => p.renego === 'shift').length;

  return (
    <Layout title="Suggested Negotiations" subtitle="Identify and action highest-impact renegotiation opportunities across Live BOMs">
      <div className="space-y-6" data-testid="part-renegotiations-page">
        {/* Header KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-green-600 uppercase flex items-center">Total Savings (Anchor) <InfoTip text="Sum of per-component savings if each part is renegotiated to the Negotiation Anchor price." /></p>
              <p className="text-2xl font-bold text-green-700">${totalValueAnchor.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Best Rate: <span className="font-semibold text-green-700">${totalValueBest.toLocaleString()}</span></p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-gray-500 uppercase">Parts to Renegotiate</p>
              <p className="text-2xl font-bold text-gray-900">{filtered.length}</p>
              <p className="text-xs text-gray-400 mt-1">From {allBoms.length} Live BOMs</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-blue-600 uppercase flex items-center"><RefreshCw className="h-3 w-3 mr-1" /> Renegotiate (Same Supplier)</p>
              <p className="text-2xl font-bold text-blue-700">{sameSupplierCount}</p>
              <p className="text-xs text-blue-400 mt-1">Negotiate better terms</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-amber-600 uppercase flex items-center"><TrendingDown className="h-3 w-3 mr-1" /> Suggest Supplier Shift</p>
              <p className="text-2xl font-bold text-amber-700">{shiftSupplierCount}</p>
              <p className="text-xs text-amber-400 mt-1">Better options available</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex gap-3 items-center flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input placeholder="Search MPN, distributor, category..." value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} className="pl-10" data-testid="renego-search" />
              </div>
              <Select value={bomFilter} onValueChange={v => { setBomFilter(v); setPage(1); }}>
                <SelectTrigger className="w-52 h-9" data-testid="bom-filter">
                  <SelectValue placeholder="BOM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Live BOMs</SelectItem>
                  {allBoms.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <DateRangeSelect value={dateRange} onChange={v => { setDateRange(v); setPage(1); }} testId="date-range-filter" />
              <Input type="number" placeholder="Min savings ($)" value={minSavings} onChange={e => { setMinSavings(e.target.value); setPage(1); }} className="w-40 h-9" data-testid="min-savings-filter" />
              <Button variant="outline" size="sm" className="border-green-300 text-green-700"><Download className="h-4 w-4 mr-1" /> PDF</Button>
            </div>
          </CardContent>
        </Card>

        {/* Component Table */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                Component-wise Renegotiation Opportunities
                <Badge variant="outline">{filtered.length} parts</Badge>
                <InfoTip text="Sorted by value impact (Negotiation Anchor savings, descending). Efficiency = best-rate / your-price. Efficacy combines price + reliability. BOM Reference shows which Live BOM the part belongs to." />
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>MPN</TableHead>
                  <TableHead>BOM Reference</TableHead>
                  <TableHead>Distributor</TableHead>
                  <TableHead>Total Spend</TableHead>
                  <TableHead>Efficiency</TableHead>
                  <TableHead>Best Rate</TableHead>
                  <TableHead>Anchor</TableHead>
                  <TableHead>Savings (Anchor)</TableHead>
                  <TableHead>Trend</TableHead>
                  <TableHead>Distrib Eff/Efficacy</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((p, idx) => (
                  <TableRow key={idx} data-testid={`renego-row-${idx}`}>
                    <TableCell className="font-mono font-medium text-sm">{p.mpn}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{p.bomRef}</Badge></TableCell>
                    <TableCell className="text-sm">{p.distributor}</TableCell>
                    <TableCell className="font-semibold">${p.totalSpend.toLocaleString()}</TableCell>
                    <TableCell><Badge className={effBadge(p.efficiency)}>{p.efficiency}%</Badge></TableCell>
                    <TableCell className="text-green-700 font-semibold">${p.bestRate.toFixed(3)}</TableCell>
                    <TableCell className="text-blue-700 font-semibold">${p.anchor.toFixed(3)}</TableCell>
                    <TableCell>
                      <span className="font-bold text-green-600">${p.savingsAnchor.toLocaleString()}</span>
                      <span className="text-xs text-gray-400 ml-1">({((p.savingsAnchor / p.totalSpend) * 100).toFixed(1)}%)</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-8">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={p.trendData}>
                              <Tooltip formatter={v => [`$${Number(v).toFixed(3)}`, 'Price']} />
                              <Area type="monotone" dataKey="p" stroke={p.trend > 0 ? '#ef4444' : '#16a34a'} fill={p.trend > 0 ? '#fee2e2' : '#dcfce7'} strokeWidth={1.5} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                        <Badge className={p.trend > 0 ? 'bg-red-100 text-red-800 text-[10px]' : p.trend < 0 ? 'bg-green-100 text-green-800 text-[10px]' : 'bg-gray-100 text-gray-600 text-[10px]'}>
                          {p.trend > 0 ? '+' : ''}{p.trend}%
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs space-y-0.5">
                        <div>Eff: <span className="font-semibold" style={{ color: p.distribEff >= 80 ? '#16a34a' : p.distribEff >= 60 ? '#f59e0b' : '#ef4444' }}>{p.distribEff}%</span></div>
                        <div>Efcy: <span className="font-semibold" style={{ color: p.distribEfficacy >= 80 ? '#16a34a' : p.distribEfficacy >= 60 ? '#f59e0b' : '#ef4444' }}>{p.distribEfficacy}%</span></div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={p.renego === 'same' ? 'bg-blue-100 text-blue-800 text-[10px]' : 'bg-amber-100 text-amber-800 text-[10px]'}>
                        {p.renego === 'same' ? 'Renegotiate' : 'Shift'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <QuickActions mpn={p.mpn} />
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => navigate('/component-intelligence')} data-testid={`detail-${p.mpn}`}>
                          Detail <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t" data-testid="pagination">
                <p className="text-sm text-gray-500">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} parts
                </p>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="prev-page">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                      className={p === page ? 'bg-green-600 text-white hover:bg-green-700' : ''}
                      onClick={() => setPage(p)} data-testid={`page-${p}`}>
                      {p}
                    </Button>
                  ))}
                  <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)} data-testid="next-page">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />
    </Layout>
  );
};

export default PartRenegotiations;
